import { useEffect, useState } from 'preact/hooks';
import {
  Button,
  Icon,
  type ButtonProps
} from '@nespresso-design-system/components';
import type { VNode } from 'preact';
import styles from './Loader.module.scss';

type LoaderErrorType = {
  error: Error;
  onRetry: () => void;
};

type LoaderProps<T> = {
  load: () => Promise<T>;
  ttl?: number;
  showSkeleton?: boolean;
  children: (props: T) => VNode;
  renderSkeleton: () => VNode;
  pbSelector?: string;
  renderError?: (error: LoaderErrorType) => VNode;
};

const DEFAULT_TTL = 1000 * 60; // 1 min
const TIMEOUT_ERROR = new Error(
  `Request timed out: The server took too long to respond and the request could not be processed.`
);

export default function Loader<T>({
  load,
  ttl = DEFAULT_TTL,
  children,
  showSkeleton = true,
  renderSkeleton,
  pbSelector,
  renderError = ({ error, onRetry }) => (
    <LoaderError error={error} onRetry={onRetry} />
  )
}: LoaderProps<T>) {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<LoaderErrorType | null>(null);
  const [unmounted, setUnmounted] = useState(false);

  useEffect(() => {
    const hasPageBuilder = pbSelector && customElements.get(pbSelector);
    const unmountHandler = () => setUnmounted(true);
    const manualRetry = new URLSearchParams(window.location.search).has(
      'debug'
    );
    let timeout: ReturnType<typeof setTimeout>;

    const loadData = async () => {
      try {
        setData(null);
        setError(null);

        const timeoutPromise = new Promise<never>((_, reject) => {
          timeout = setTimeout(() => {
            reject(TIMEOUT_ERROR);
          }, ttl);
        });

        const result = await Promise.race([load(), timeoutPromise]);
        setData(result);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error(err);
        clearTimeout(timeout);

        if (manualRetry) {
          setError({ error: err as Error, onRetry: loadData });
        } else {
          timeout = setTimeout(loadData, err === TIMEOUT_ERROR ? 0 : ttl);
        }
      }
    };

    loadData();

    if (hasPageBuilder) {
      window.addEventListener('preact:unmount', unmountHandler);
    }

    return () => {
      clearTimeout(timeout);

      if (hasPageBuilder) {
        window.removeEventListener('preact:unmount', unmountHandler);
      }
    };
  }, []);

  if (error) return renderError(error);
  if (!data) return showSkeleton ? renderSkeleton() : null;

  // Note: When the web component is disconnected from Page Builder,
  // it is necessary to clean up everything related to Preact. By
  // returning null here, we trigger Preact's internal cleanup to
  // avoid having disconnected nodes, such as portals, etc.
  if (unmounted) return null;

  return children(data);
}

// Note: There is NOT i18n yet at this level
function LoaderError({ error, onRetry }: LoaderErrorType) {
  return (
    <div class={styles.container}>
      <div>
        <hgroup>
          <Icon
            method="image"
            icon="32/symbol/status-alert"
            title="Something went wrong"
          />
          <h2>Something went wrong</h2>
          <p>
            There seems to be a technical issue. Please wait a few minutes and
            retry.
          </p>
        </hgroup>
        <Button
          label="Retry"
          className={styles.retry}
          size={'medium' as ButtonProps['size']}
          type="button"
          variant="outline"
          contrast="light"
          onClick={onRetry}
        >
          Retry
        </Button>
      </div>
      <div class={styles.details}>
        {error?.message}
        {error?.cause && (
          <details open>
            <summary>Technical details</summary>
            <iframe
              title="Error details"
              srcDoc={(error.cause as Error).message}
              width="100%"
              height={500}
            />
          </details>
        )}
      </div>
    </div>
  );
}
